// ONDEMAND-1: the search results' "these meetings aren't transcribed yet" section. Full-text
// search can only see transcripts, so deferred recordings that match by title/tag/folder are
// listed here with a cost preview and ONE explicit "Transcribe these" button. Searching never
// triggers a transcription on its own — only that click does. Rendered only when the feature
// is enabled.
import { useState } from 'react';
import type { BatchTranscribeResponse } from '@scribe/core/types';
import { useTranscribeBatch, useUntranscribedSearch } from '../../lib/ondemandQueries';
import { formatEstimatedMinutes, formatUsd } from '../ui';

/** The server caps a batch at 10 ids regardless of what `max_batch` says. */
const HARD_BATCH_CAP = 10;

/** "Transcribing 3 — about 5 min. Search again then." plus any budget/other messages. */
function outcomeSummary(result: BatchTranscribeResponse): string[] {
  const lines: string[] = [];
  if (result.triggered_count > 0) {
    const etaSec = Math.max(
      0,
      ...result.items.filter((i) => i.outcome === 'triggered').map((i) => i.eta_seconds ?? 0),
    );
    const eta = etaSec > 0 ? ` — about ${Math.max(1, Math.round(etaSec / 60))} min` : '';
    lines.push(`Transcribing ${result.triggered_count}${eta}. Search again then.`);
  }
  const blocked = new Set(
    result.items.filter((i) => i.outcome === 'budget_blocked').map((i) => i.message),
  );
  lines.push(...blocked);
  if (lines.length === 0) lines.push('Nothing new to transcribe — these are already on their way.');
  return lines;
}

export function UntranscribedMatches({ q, onOpen }: { q: string; onOpen: (id: string) => void }) {
  const { data } = useUntranscribedSearch(q, true);
  const batch = useTranscribeBatch();
  // The outcome belongs to the query it was triggered from; a new query clears it.
  const [outcomeFor, setOutcomeFor] = useState<string | null>(null);

  const trimmed = q.trim();
  const result = outcomeFor === trimmed ? batch.data : undefined;

  // After a batch the refetched search usually comes back empty (they're no longer deferred)
  // — keep the outcome visible rather than the whole section vanishing with it.
  if (result && (!data || data.total === 0)) {
    return (
      <section className="ondemand-matches" aria-label="Recordings not transcribed yet">
        <BatchOutcome result={result} />
      </section>
    );
  }
  if (!data || data.total === 0) return null;

  const shown = data.items.slice(0, Math.min(data.max_batch, HARD_BATCH_CAP));

  return (
    <section className="ondemand-matches" aria-label="Recordings not transcribed yet">
      <p>
        <strong>
          {data.total} meeting{data.total === 1 ? '' : 's'} matching “{trimmed}”{' '}
          {data.total === 1 ? "isn't" : "aren't"} transcribed yet
        </strong>
      </p>
      <ul className="ondemand-matches-list">
        {shown.map((m) => (
          <li key={m.recording_id}>
            <a
              role="button"
              tabIndex={0}
              onClick={() => onOpen(m.recording_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(m.recording_id);
                }
              }}
            >
              {m.title}
            </a>
            {m.estimated_duration_sec != null && (
              <span className="muted" title="Estimated duration">
                {' '}
                · {formatEstimatedMinutes(m.estimated_duration_sec)}
              </span>
            )}
          </li>
        ))}
      </ul>
      {result ? (
        <BatchOutcome result={result} />
      ) : (
        <button
          className="sm"
          disabled={batch.isPending}
          onClick={() => {
            setOutcomeFor(trimmed);
            batch.mutate(shown.map((m) => m.recording_id));
          }}
        >
          {batch.isPending
            ? 'Starting…'
            : `Transcribe ${shown.length === 1 ? 'this' : 'these'} (≈${data.estimated_minutes} min, ≈${formatUsd(data.estimated_cost_usd)})`}
        </button>
      )}
      {batch.isError && outcomeFor === trimmed && (
        <p className="error">{(batch.error as Error).message}</p>
      )}
    </section>
  );
}

function BatchOutcome({ result }: { result: BatchTranscribeResponse }) {
  return (
    <div role="status">
      {outcomeSummary(result).map((line) => (
        <p key={line} className="muted">
          {line}
        </p>
      ))}
    </div>
  );
}
